import React, { useEffect, useState } from 'react';
import AnnAvatar from '../../images/AnnAvatar.svg';
import UserAvatar from '../../images/UserAvatar.svg';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import DOMPurify from 'dompurify';
import {
  MessageContainer,
  MessageWrapper,
  ChatWindowAvatar,
  MessageButtonsWrapper,
  EditMessageWrapper,
  EditMessageText,
  EditMessageInput,
  EditMessageInputWrapper,
  MessageInfo
} from './ChatBox.styled';
import { Icon, RoundedButton } from '../';
import { observer } from "mobx-react";
import { MarkdownStyles } from './MarkdownStyles';

const Message = observer(({ messageItem, key, displayButtons, width, selectedAgent }) => {
  const [displayMessageButtons, setDisplayMessageButtons] = useState(displayButtons)
  const [displayMessageView, setDisplayMessageView] = useState(null)

  const createMarkup = (content) => {
    return { __html: DOMPurify.sanitize(content, {
      ALLOWED_ATTR: ['class', 'id', 'style'],
      ALLOWED_CSS_PROPERTIES: [
        'color',
        'background-color',
        'font-weight',
        'font-style',
        'text-decoration',
        'text-align',
        'font-size'
      ]
    }) };
  };

  const isHTML = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };

  return(
    <>
      <MarkdownStyles />
      <MessageContainer key={key} itemType={messageItem.type} width={width}>
        { messageItem.type === "sent" ?
            <ChatWindowAvatar src={UserAvatar} alt="Agent Avatar" /> :
            <ChatWindowAvatar src={selectedAgent?.photo_url || AnnAvatar} alt="Agent Avatar" />
        }

        {!displayMessageView &&
            <MessageWrapper itemType={messageItem.type}>
              <MessageInfo>
                {messageItem.type === "sent" ? `${messageItem && messageItem.time}` : `${selectedAgent ? selectedAgent.name : ''} - ${messageItem && messageItem.time}`}
              </MessageInfo>
              <div className={`markdown-content ${messageItem.isTyping ? 'streaming-message' : ''}`}>
                {(() => {
                  // Type validation to prevent rendering objects
                  let messageContent = messageItem.message;
                  
                  if (typeof messageContent !== 'string') {
                    console.error('Message is not a string:', messageContent);
                    // Try to extract string from object
                    if (typeof messageContent === 'object' && messageContent !== null) {
                      if (messageContent.message) messageContent = messageContent.message;
                      else if (messageContent.content) messageContent = messageContent.content;
                      else if (messageContent.text) messageContent = messageContent.text;
                      else {
                        return (
                          <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
                            Error rendering message: Invalid message format
                          </div>
                        );
                      }
                    } else {
                      return <div style={{ color: 'red' }}>Unable to display message</div>;
                    }
                  }

                  return (
                    <>
                      {isHTML(messageContent) ? (
                        <div 
                          dangerouslySetInnerHTML={createMarkup(messageContent)}
                          className="html-content"
                        />
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[
                            remarkGfm,
                            remarkParse,
                            [remarkRehype, {
                              singleTilde: true,
                              stringLength: true,
                              tablePipeAlign: true,
                              tableCellPadding: true
                            }]
                          ]}
                          rehypePlugins={[
                            rehypeRaw,
                            rehypeStringify,
                            [rehypeSanitize, {
                              allowElements: [
                                'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
                                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                                'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
                                'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
                                'div', 'span', 'del'
                              ],
                              allowAttributes: {
                                'a': ['href', 'target', 'rel'],
                                'img': ['src', 'alt', 'title'],
                                '*': ['class']
                              }
                            }]
                          ]}
                          components={{
                            table: ({node, ...props}) => (
                              <table className="markdown-table" {...props} />
                            ),
                            thead: ({node, ...props}) => (
                              <thead className="markdown-thead" {...props} />
                            ),
                            tbody: ({node, ...props}) => (
                              <tbody className="markdown-tbody" {...props} />
                            ),
                            tr: ({node, ...props}) => (
                              <tr className="markdown-tr" {...props} />
                            ),
                            th: ({node, ...props}) => (
                              <th className="markdown-th" {...props} />
                            ),
                            td: ({node, ...props}) => (
                              <td className="markdown-td" {...props} />
                            ),
                            a: ({node, ...props}) => (
                              <a 
                                className="gfm-link" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                {...props} 
                              />
                            ),
                            input: ({node, ...props}) => {
                              if (props.type === 'checkbox') {
                                return (
                                  <input 
                                    className="gfm-checkbox" 
                                    {...props} 
                                    readOnly 
                                  />
                                )
                              }
                              return <input {...props} />
                            },
                            del: ({node, ...props}) => (
                              <del className="gfm-strikethrough" {...props} />
                            ),
                            li: ({node, className, ...props}) => {
                              if (className?.includes('task-list-item')) {
                                return <li className="gfm-task-list-item" {...props} />
                              }
                              return <li {...props} />
                            },
                            div: ({node, ...props}) => <div {...props} />,
                            span: ({node, ...props}) => <span {...props} />,
                            img: ({node, ...props}) => <img {...props} alt={props.alt || ''} />,
                            p: ({node, ...props}) => <p {...props} />,
                            br: ({node, ...props}) => <br {...props} />,
                            hr: ({node, ...props}) => <hr {...props} />
                          }}
                        >
                          {messageContent}
                        </ReactMarkdown>
                      )}
                      {messageItem.isTyping && (
                        <span className="typing-cursor"></span>
                      )}
                    </>
                  );
                })()}
              </div>
              {messageItem.type !== 'sent' && displayMessageButtons &&
                  <MessageButtonsWrapper>
                    <a onClick={() => setDisplayMessageButtons(false)}>
                      <Icon name={"thumbsUp"} height={'15px'} width={'15px'} />
                    </a>
                    <a onClick={() => setDisplayMessageView('initial')}>
                      <Icon name={"thumbsDown"} height={'15px'} width={'15px'} />
                    </a>
                  </MessageButtonsWrapper>
              }
            </MessageWrapper>
        }

        {messageItem.type !== 'sent' && displayMessageView === 'initial' && (
            <EditMessageWrapper>
              <Icon name={"head1"} height={'120px'} width={'120px'} />
              <EditMessageText>Help me to improve!</EditMessageText>
              <RoundedButton width='20%' onClick={() => setDisplayMessageView('edit')}>Train me</RoundedButton>
            </EditMessageWrapper>
        )}

        {messageItem.type !== 'sent' && displayMessageView === 'edit' && (
            <EditMessageInputWrapper>
              <EditMessageInput placeholder='Feel free to enter the correct answer right here.' rows="10" cols="50"></EditMessageInput>

              <MessageButtonsWrapper mt='5px'>
                <Icon name={"thumbsDown"} height={'15px'} width={'15px'} />
                <RoundedButton width='150px' height='40px' fontSize='15px' onClick={() => {setDisplayMessageView(null);setDisplayMessageButtons(false)}}>Train</RoundedButton>
              </MessageButtonsWrapper>
            </EditMessageInputWrapper>
        )}
      </MessageContainer>
    </>
  )
});

export default Message;